import React from 'react';
import { Box, Text, Image, HStack, Flex, Icon, Button, RadioGroup, Radio, Stack } from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import {
  FaFile, FaFilePdf, FaFileImage, FaFileWord, FaFileExcel,
  FaEye, FaFileAudio, FaFileVideo, FaFileCode, FaFileAlt,
} from 'react-icons/fa';

const HEIC_EXTENSIONS = ['heic', 'heif'];

const FILE_EXTENSIONS = [
  'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'ico', 'tiff', ...HEIC_EXTENSIONS,
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
  'mp3', 'wav', 'ogg', 'aac', 'm4a', 'flac',
  'mp4', 'webm', 'avi', 'mov', 'wmv', 'flv', 'm4v',
  'txt', 'csv', 'json', 'xml', 'html', 'htm', 'md', 'log',
  'js', 'css', 'php', 'py', 'java', 'cpp', 'c', 'h', 'sql', 'yaml', 'yml',
  'zip', 'rar', '7z', 'tar', 'gz',
];

export const isFileResponse = (answer) => {
  if (!answer || typeof answer !== 'string') return false;
  const extension = answer.split('.').pop().toLowerCase();
  return FILE_EXTENSIONS.includes(extension);
};

const FILE_ICON_MAP = {
  pdf: FaFilePdf,
  jpg: FaFileImage, jpeg: FaFileImage, png: FaFileImage, gif: FaFileImage,
  webp: FaFileImage, bmp: FaFileImage, svg: FaFileImage, ico: FaFileImage, tiff: FaFileImage,
  heic: FaFileImage, heif: FaFileImage,
  doc: FaFileWord, docx: FaFileWord,
  xls: FaFileExcel, xlsx: FaFileExcel,
  mp3: FaFileAudio, wav: FaFileAudio, ogg: FaFileAudio, aac: FaFileAudio, m4a: FaFileAudio, flac: FaFileAudio,
  mp4: FaFileVideo, webm: FaFileVideo, avi: FaFileVideo, mov: FaFileVideo, wmv: FaFileVideo, flv: FaFileVideo, m4v: FaFileVideo,
  js: FaFileCode, css: FaFileCode, php: FaFileCode, py: FaFileCode, java: FaFileCode,
  cpp: FaFileCode, c: FaFileCode, h: FaFileCode, sql: FaFileCode, yaml: FaFileCode, yml: FaFileCode,
  txt: FaFileAlt, csv: FaFileAlt, json: FaFileAlt, xml: FaFileAlt, html: FaFileAlt, htm: FaFileAlt, md: FaFileAlt, log: FaFileAlt,
};

export const getFileIcon = (filename) => {
  if (!filename) return FaFile;
  const extension = filename.split('.').pop().toLowerCase();
  return FILE_ICON_MAP[extension] || FaFile;
};

export const buildFileUrl = (path, apiUrlBase) => {
  if (!path) return '';
  const cleanPath = path.replace(/\\/g, '');
  const isFullUrl = cleanPath.startsWith('http://') || cleanPath.startsWith('https://');
  return isFullUrl ? cleanPath : `${apiUrlBase}${cleanPath}`;
};

export const isHeicFile = (path) => {
  if (!path || typeof path !== 'string') return false;
  const extension = path.split('.').pop().toLowerCase();
  return HEIC_EXTENSIONS.includes(extension);
};

// HEIC/HEIF no se puede renderizar en un <img> del navegador: se pide ya convertida a JPG a datofy-api.
export const buildImagePreviewUrl = (path, apiUrlBase) => {
  if (!path) return '';
  if (isHeicFile(path)) {
    return `${apiUrlBase}/api/files/heic-preview?path=${encodeURIComponent(path)}`;
  }
  return buildFileUrl(path, apiUrlBase);
};

// Fila de archivo adjunto — usada para respuestas de tipo "file" o texto que resulta ser una ruta de archivo
const FileAnswer = ({ answer, apiUrlBase, onPreviewFile }) => {
  const FileIcon = getFileIcon(answer);
  return (
    <HStack spacing={3} align="center" bg="cream.bg2" borderRadius="12px" p="10px 12px">
      <Flex w="36px" h="36px" borderRadius="10px" bg="coral.soft" color="coral.700"
        align="center" justify="center" flexShrink={0}>
        <Icon as={FileIcon} boxSize={4} />
      </Flex>
      <Box flex={1} minW={0}>
        <Text fontSize="13px" fontWeight="700" color="ink.900" noOfLines={1}>
          {answer.split('/').pop()}
        </Text>
        <Text fontSize="11px" color="ink.400" fontWeight="600">
          {answer.split('.').pop().toUpperCase()}
        </Text>
      </Box>
      <HStack spacing={2} flexShrink={0}>
        {onPreviewFile && (
          <Button size="xs" bg="coral.500" color="white" borderRadius="8px" fontWeight="700"
            _hover={{ bg: 'coral.600' }} leftIcon={<Icon as={FaEye} boxSize="11px" />}
            onClick={() => onPreviewFile(answer)}>
            Ver
          </Button>
        )}
        <Button size="xs" as="a" href={buildFileUrl(answer, apiUrlBase)} target="_blank" rel="noopener noreferrer"
          variant="outline" borderColor="cream.line2" color="ink.700" borderRadius="8px" fontWeight="700"
          rightIcon={<ExternalLinkIcon boxSize="11px" />}>
          Descargar
        </Button>
      </HStack>
    </HStack>
  );
};

/**
 * Renderiza el valor de una respuesta de ficha según el tipo de pregunta.
 * Componente presentacional puro — usado por el modal de fichas y por el panel
 * de previsualización inline para no duplicar esta lógica en cada uno.
 */
const FichaFieldValue = ({ question, apiUrlBase = '', onPreviewFile }) => {
  const answer = question?.response?.answer;
  const typeName = question?.type?.name;

  if (typeName === 'signature') {
    return answer ? (
      <Image src={buildFileUrl(answer, apiUrlBase)} alt="Firma" maxW="260px" mx="auto" display="block" />
    ) : (
      <Text fontSize="13px" color="ink.400" fontStyle="italic">Sin firma</Text>
    );
  }

  if (typeName === 'radio') {
    const choices = question.response_choices?.length ? question.response_choices : [{ text: answer || 'Sin respuesta' }];
    return (
      <RadioGroup value={answer || ''}>
        <Stack direction="row" spacing={4} flexWrap="wrap">
          {choices.map((option, i) => (
            <Radio key={i} value={option.text} isReadOnly colorScheme="orange" size="sm">
              <Text fontSize="13.5px" fontWeight="600" color="ink.900">{option.text}</Text>
            </Radio>
          ))}
        </Stack>
      </RadioGroup>
    );
  }

  if (isFileResponse(answer)) {
    return <FileAnswer answer={answer} apiUrlBase={apiUrlBase} onPreviewFile={onPreviewFile} />;
  }

  return (
    <Text fontSize="13.5px" fontWeight="600" whiteSpace="pre-wrap"
      color={answer ? 'ink.900' : 'ink.400'} fontStyle={answer ? 'normal' : 'italic'}>
      {answer || 'Sin respuesta'}
    </Text>
  );
};

export default FichaFieldValue;
